import React, { useState, useCallback } from 'react';
import {
  DillModal,
  DillButton,
  DillInput,
  DillField,
  DillCard,
  DillToggle,
} from '../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  createMonitoringPortfolio,
  getSavedMonitoringPortfolios,
} from '../../../../redux/services/creditReportService';
import { openDillToast } from '../../../../utils/helpers';

const defaultEmail = () => ({ id: Date.now(), firstName: '', lastName: '', emailAddress: '' });

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const AddPortfolioModal = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [emails, setEmails] = useState([{ id: 1, firstName: '', lastName: '', emailAddress: '' }]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ portfolioName?: string; emails?: Record<number, string> }>(
    {}
  );

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const addEmail = () => setEmails((e) => [...e, defaultEmail()]);
  const removeEmail = (id: number) =>
    setEmails((e) => (e.length > 1 ? e.filter((x) => x.id !== id) : e));
  const updateEmail = (id: number, field: string, value: string) => {
    setEmails((e) => e.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
    setErrors({});
  };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.portfolioName = 'Portfolio name is required';
    const emailErrors: Record<number, string> = {};
    emails.forEach((e) => {
      if (!e.firstName.trim() || !e.lastName.trim() || !isValidEmail(e.emailAddress)) {
        emailErrors[e.id] = 'All fields must be filled with valid email';
      }
    });
    if (Object.keys(emailErrors).length) newErrors.emails = emailErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCloseModal = () => {
    setLoading(false);
    setName('');
    setIsDefault(false);
    setEmails([defaultEmail()]);
    setErrors({});
    handleClose();
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setLoading(true);
    const body = {
      name,
      isDefault,
      emails: emails.map(({ firstName, lastName, emailAddress }) => ({
        firstName,
        lastName,
        emailAddress,
      })),
      emailSubject: 'Creditsafe Monitoring Notification on portfolio {{portfolioName}}',
      emailLanguage: 'en',
      frequency: '1',
    };
    try {
      const res = await dispatch(
        createMonitoringPortfolio({ supplierId: currentSupplier?.id ?? '', details: body })
      );
      if (res.type.includes('fulfilled')) {
        await dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));
        openDillToast({ message: 'Successfully added portfolio', type: 'SUCCESS' });
      }
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  }, [name, isDefault, emails]);

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Add Portfolio"
      footer={
        <>
          <DillButton label="Close" onClick={handleCloseModal} variant="secondary" />
          <DillButton
            label="Create Portfolio →"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={loading}
          />
        </>
      }>
      {/* Portfolio Settings */}
      <DillCard title="Portfolio settings">
        <DillField label="Portfolio Name" error={errors.portfolioName} required>
          <DillInput
            placeholder="My Portfolio"
            value={name}
            onChange={(text) => {
              setName(text);
              setErrors({});
            }}
          />
        </DillField>

        <div className="mt-3.5">
          <label className="mb-1.5 block text-[12px] text-slate-500">Set as default</label>
          <div className="flex items-center gap-3">
            <DillToggle value={isDefault} onChange={setIsDefault} />
            <span className={`text-xs ${isDefault ? 'text-blue-600' : 'text-gray-500'}`}>
              {isDefault ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </DillCard>

      {/* Email Recipients */}
      <DillCard title="Email recipients">
        <div className="space-y-2">
          {emails.map((email) => (
            <div key={email.id} className="rounded-lg border border-gray-200 p-3">
              <div className="mb-2 grid grid-cols-2 gap-2">
                <DillField label="First name" required>
                  <DillInput
                    value={email.firstName}
                    onChange={(text) => updateEmail(email.id, 'firstName', text)}
                  />
                </DillField>
                <DillField label="Last name" required>
                  <DillInput
                    value={email.lastName}
                    onChange={(text) => updateEmail(email.id, 'lastName', text)}
                  />
                </DillField>
              </div>
              <div className="flex items-end gap-2">
                <DillField label="Email address" required className="flex-1">
                  <DillInput
                    value={email.emailAddress}
                    onChange={(text) => updateEmail(email.id, 'emailAddress', text)}
                  />
                </DillField>
                {emails.length > 1 && (
                  <DillButton
                    onClick={() => removeEmail(email.id)}
                    label="Remove"
                    variant="secondary"
                  />
                )}
              </div>
              {errors.emails?.[email.id] && (
                <p className="text-red-500 text-xs mt-1">{errors.emails[email.id]}</p>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={addEmail}
          className="mt-3 w-full rounded-md border border-dashed border-slate-800 py-2 text-[11px] uppercase tracking-widest text-blue-500 transition-colors hover:border-blue-800">
          + add recipient
        </button>
      </DillCard>

      {(errors.emails || errors.portfolioName) && (
        <p className="text-red-500 text-xs text-center">See errors above</p>
      )}
    </DillModal>
  );
};

export default AddPortfolioModal;
